import { api } from './client';
import type {
  BacktestCreateRequest,
  BacktestEventDto,
  BacktestRunDto,
  TradeReplayDto,
} from './types';

export interface BacktestInputTimelineEntry {
  key: string;
  value: unknown | null;
  effectiveFrom: string;
}

export interface BacktestSessionPendingChange {
  key: string;
  value: unknown | null;
  effectiveFrom: string;
}

export interface BacktestEquityPoint {
  timeUtc: string;
  equity: number;
}

export interface BacktestSessionDeclaredInput {
  key: string;
  kind: 'Number' | 'Boolean' | 'Direction' | 'Text';
  label: string | null;
  defaultValue: unknown;
}

export interface BacktestSessionDto {
  sessionId: string;
  strategyName: string;
  datasetIds: string[];
  accountSize: number;
  traceLevel: string;
  createdUtc: string;
  firstTimeUtc: string;
  lastTimeUtc: string;
  currentTimeUtc: string | null;
  completed: boolean;
  committedRunId: string | null;
  sourceRunId: string | null;
  equity: number;
  peakEquity: number;
  realizedPnl: number;
  maxDrawdown: number;
  maxDrawdownPercent: number;
  openPositionCount: number;
  effectiveInputs: Record<string, unknown>;
  pendingChanges: BacktestSessionPendingChange[];
  changeLog: BacktestInputTimelineEntry[];
  timeframes: { id: string; name: string; duration: string }[];
  declaredInputs: BacktestSessionDeclaredInput[];
}

/** One advance/restart: newly computed evidence plus the refreshed engine readout. */
export interface BacktestSessionFrame {
  currentTimeUtc: string | null;
  completed: boolean;
  events: BacktestEventDto[];
  trades: TradeReplayDto[];
  equityPoints: BacktestEquityPoint[];
  equity: number;
  peakEquity: number;
  realizedPnl: number;
  maxDrawdown: number;
  maxDrawdownPercent: number;
  openPositionCount: number;
  effectiveInputs: Record<string, unknown>;
  pendingChanges: BacktestSessionPendingChange[];
}

export interface BacktestSessionEvidence {
  currentTimeUtc: string | null;
  completed: boolean;
  events: BacktestEventDto[];
  trades: TradeReplayDto[];
  equityPoints: BacktestEquityPoint[];
}

export interface BacktestSessionInputsResult {
  effectiveInputs: Record<string, unknown>;
  effectiveFromUtc: string;
  pendingChanges: BacktestSessionPendingChange[];
}

/** Engine account readout at the computed cursor. */
export interface BacktestSessionControllerReadout {
  equity: number;
  peakEquity: number;
  realizedPnl: number;
  maxDrawdown: number;
  maxDrawdownPercent: number;
  openPositionCount: number;
}

function sessionPath(sessionId: string) {
  return `/api/backtests/sessions/${encodeURIComponent(sessionId)}`;
}

export const backtestSessions = {
  create: (request: BacktestCreateRequest) =>
    api.post<BacktestSessionDto>('/api/backtests/sessions', request),
  createFromRun: (runId: string) =>
    api.post<BacktestSessionDto>(`/api/backtests/${encodeURIComponent(runId)}/session`),
  get: (sessionId: string, signal?: AbortSignal) =>
    api.get<BacktestSessionDto>(sessionPath(sessionId), { signal }),
  evidence: (sessionId: string, signal?: AbortSignal) =>
    api.get<BacktestSessionEvidence>(`${sessionPath(sessionId)}/evidence`, { signal }),
  advance: (sessionId: string, request: { toTimeUtc?: string | null; maxBars?: number | null }) =>
    api.post<BacktestSessionFrame>(`${sessionPath(sessionId)}/advance`, request),
  setInputs: (sessionId: string, values: Record<string, unknown | null>) =>
    api.put<BacktestSessionInputsResult>(`${sessionPath(sessionId)}/inputs`, { values }),
  restart: (sessionId: string, startFromUtc?: string | null) =>
    api.post<BacktestSessionFrame>(`${sessionPath(sessionId)}/restart`, { startFromUtc: startFromUtc ?? null }),
  commit: (sessionId: string) =>
    api.post<BacktestRunDto>(`${sessionPath(sessionId)}/commit`),
  discard: (sessionId: string) =>
    api.delete<void>(sessionPath(sessionId)),
};
